import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import { FormContainer as Form } from 'components/shared/form/Form';
import FormHeader from 'components/shared/form/FormHeader';
import FormSection from 'components/shared/form/FormSection';
import { MultiSelectChips, Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import { useProfile } from 'context/profile';
import { useAdminForm } from 'hooks/datasource';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import Credential from 'models/flexible/Credential';
import { PERMISSIONS } from 'models/flexible/Profile';
import { Role } from 'models/flexible/User';
import { adminIndexClients } from 'services/api/admin/clients';
import { adminIndexCredentials } from 'services/api/admin/credentials';
import { adminIndexGroups } from 'services/api/admin/groups';
import { indexGroupTypes } from 'services/api/admin/groupTypes';
import { adminIndexRoles } from 'services/api/admin/roles';
import { adminCreateUser, adminShowUser, adminUpdateUser, adminUsersQueryKeys } from 'services/api/admin/users';
import { useToastActions } from 'stores/toastStore';

// ---------------------------------------------------------------------------
// Constants + helpers
// ---------------------------------------------------------------------------

const getId = (o: any) => o!.id as string;
const getName = (o: any) => o!.name as string;
const getDisplayName = (o: any) => o!.displayName as string;

const ADMIN_CLIENT_TYPE = 'Admin';
const FORBIDDEN_ROLES = ['Olio Admin', 'View Only Olio Admin', 'Bot'];
const FORBIDDEN_ROLES_ADMIN = ['Bot'];

function validate(values: any) {
  const errors: Record<string, string> = {};
  if (!values.name) errors.name = 'User name is required';
  if (!values.email) {
    errors.email = 'Valid email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
    errors.email = 'Valid email is required';
  } else if (values.client?.clientType === ADMIN_CLIENT_TYPE && !values.email.endsWith('@olio.health')) {
    errors.email = 'Users associated to an Admin client must use the "@olio.health" domain';
  }
  if (!values.role?.id) errors.role = 'Role is required';
  if (!values.client?.id) errors.client = 'Client is required';
  return errors;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function EditUser() {
  const { id } = useParams<{ id?: string }>();
  const isEditing = !!id;
  const { addToast } = useToastActions();
  const { profile } = useProfile();
  const canEdit = profile.has(PERMISSIONS.adminUserEdit);
  const disabled = !canEdit;

  // --- Data + form ---

  const form = useAdminForm({
    id,
    show: adminShowUser,
    create: async (payload: any) => {
      try {
        return await adminCreateUser(payload);
      } catch (err: any) {
        const message = err?.response?.data?.error || 'There was an error saving the user. Please try again.';
        addToast({ text: message });
        throw err;
      }
    },
    update: async (updateId: string, payload: any) => {
      try {
        return await adminUpdateUser(updateId, payload);
      } catch (err: any) {
        const message = err?.response?.data?.error || 'There was an error saving the user. Please try again.';
        addToast({ text: message });
        throw err;
      }
    },
    include: [
      'actingClient',
      'client',
      'credential',
      'enabledProviderTypes',
      'role',
      'selectedGroups',
      'selectedProvidedProviderTypes',
      'selectedManagedProviderTypes',
    ] as const,
    queryKey: adminUsersQueryKeys,
    validate,
    onCreateSuccess: { toast: 'User created successfully.', navigate: '/admin/users' },
    onUpdateSuccess: { toast: 'User updated successfully.', navigate: '/admin/users' },
    toFormValues: (data) => ({
      id: data?.id ?? '',
      name: data?.name ?? '',
      email: data?.email ?? '',
      credential: data?.credential ?? null,
      client: data?.client ?? null,
      actingClient: data?.actingClient ?? null,
      role: data?.role ?? null,
      groupType: data?.groupType ?? null,
      selectedManagedProviderTypes: data?.selectedManagedProviderTypes ?? [],
      selectedProvidedProviderTypes: data?.selectedProvidedProviderTypes ?? [],
      selectedGroups: data?.selectedGroups ?? [],
    }),
    toPayload: (v) => ({
      id: v.id || undefined,
      active: true,
      clientId: v.client?.id,
      credentialId: v.credential?.id || '',
      email: v.email,
      groupType: v.groupType ?? undefined,
      groupIds: v.selectedGroups.map((g) => g.id),
      name: v.name,
      roleId: v.role?.id,
      selectedManagedProviderTypeIds: v.selectedManagedProviderTypes.map((p) => p.id),
      selectedProvidedProviderTypeIds: v.selectedProvidedProviderTypes.map((p) => p.id),
      ...(v.actingClient && v.client?.id !== v.actingClient.id
        ? { actingClientId: v.actingClient.id }
        : { actingClientId: null }),
    }),
  });

  const { values, errors, valid, dirty, setValue, submit, loading, submitAndReset, goBack } = form;

  // --- Dropdown options ---

  const client = values?.client;
  const isAdmin = client?.clientType === ADMIN_CLIENT_TYPE;
  const isLeaf = !!client?.leaf;

  const clientOpts = useInfiniteOptions({
    queryKey: ['adminClients', 'users'],
    fetchFn: adminIndexClients,
    params: { sortBy: 'name asc' },
  });

  const groupOpts = useInfiniteOptions({
    queryKey: ['adminGroups', 'users', client?.id],
    fetchFn: adminIndexGroups,
    params: { clientId: client?.id, include: 'groupType', sortBy: 'name asc' },
  });

  // Static/dependent options (small datasets, not paginated)
  const [credentialOptions, setCredentialOptions] = useState<Credential[]>([]);
  const [roleOptions, setRoleOptions] = useState<Role[]>([]);
  const [groupTypes, setGroupTypes] = useState<{ kind: string; name: string; isSNF: boolean }[]>([]);

  useEffect(() => {
    adminIndexCredentials().then((r) => setCredentialOptions(r.data as Credential[]));
  }, []);

  useEffect(() => {
    if (!client?.id) {
      setGroupTypes([]);
      return;
    }
    indexGroupTypes({ clientId: client.id }).then((r) => {
      const types = r.data.map((gt) => ({
        kind: gt.apiName,
        name: gt.displayName ?? gt.apiName,
        isSNF: gt.apiName === 'skilled_nursing_facility',
      }));
      setGroupTypes(types.some((t) => t.isSNF) ? [...types].reverse() : types);
    });
  }, [client?.id]);

  useEffect(() => {
    const forbiddenList = (client?.clientType === ADMIN_CLIENT_TYPE ? FORBIDDEN_ROLES_ADMIN : FORBIDDEN_ROLES).join(
      ','
    );
    adminIndexRoles({ 'name.not': forbiddenList }).then((r) => setRoleOptions(r.data));
  }, [client?.clientType]);

  // Reset dependent fields when client changes (create mode only)
  const prevClientId = useRef<string | undefined>(client?.id);
  useEffect(() => {
    if (!isEditing && prevClientId.current !== client?.id) {
      prevClientId.current = client?.id;
      setValue('role', null);
      setValue('selectedGroups', []);
      setValue('selectedManagedProviderTypes', []);
      setValue('selectedProvidedProviderTypes', []);
      setValue('groupType', client?.isPostAcute ? 'skilled_nursing_facility' : null);
    }
  });

  const showGroupType = groupTypes.length > 0 && !client?.isPostAcute;
  const showProviderTypes = client?.isAcute
    ? ['Health System', 'ACO'].includes(client?.clientType ?? '')
    : !isAdmin && isLeaf;

  // --- Render ---

  if (isEditing && loading) return <CircularProgress />;

  return (
    <AdminFormPage>
      <FormHeader title={isEditing ? 'Edit User' : 'New User'} />
      <Form>
        <FormSection>
          <SectionHeader>Contact Information</SectionHeader>
          <InputGroup title='Name' error={errors?.name}>
            <Input
              value={values?.name ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue('name', e.target.value)}
              placeholder='User name'
              disabled={disabled}
            />
          </InputGroup>
          <InputGroup title='Credentials (optional)'>
            <Select
              options={credentialOptions}
              value={values?.credential ?? null}
              onChange={(v) => setValue('credential', v)}
              getOptionValue={getId}
              getOptionLabel={(o) => o!.title}
              isClearable
              placeholder='Select credentials'
              isDisabled={disabled}
            />
          </InputGroup>
          <InputGroup title='Email' error={errors?.email}>
            <Input
              value={values?.email ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue('email', e.target.value)}
              placeholder='your_email@example.com'
              disabled={disabled}
            />
          </InputGroup>
        </FormSection>

        <FormSection>
          <SectionHeader>User Access</SectionHeader>
          <InputGroup title='Client' error={errors?.client}>
            <Select
              {...clientOpts}
              value={values?.client ?? null}
              onChange={(v) => setValue('client', v)}
              getOptionValue={getId}
              getOptionLabel={getName}
              placeholder='Select client'
              isDisabled={disabled}
            />
          </InputGroup>
          {isEditing && (
            <InputGroup title='Current Selected View'>
              <Select
                options={[]}
                value={values?.actingClient ?? null}
                onChange={() => {}}
                getOptionValue={getId}
                getOptionLabel={getName}
                placeholder=''
                isDisabled
              />
            </InputGroup>
          )}
          <InputGroup title='Role' error={errors?.role}>
            <Select
              options={roleOptions}
              value={values?.role ?? null}
              onChange={(v) => setValue('role', v)}
              getOptionValue={getId}
              getOptionLabel={getName}
              placeholder='Select role'
              isDisabled={disabled || !client}
            />
          </InputGroup>
          {showGroupType && (
            <InputGroup title='What type of group is the user associated with?'>
              <div style={{ display: 'flex', gap: 16 }}>
                {groupTypes.map((lt) => (
                  <label key={lt.kind} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input
                      type='radio'
                      value={lt.kind}
                      checked={values?.groupType === lt.kind}
                      onChange={() => setValue('groupType', lt.kind)}
                      disabled={disabled}
                    />
                    {lt.name}
                  </label>
                ))}
              </div>
            </InputGroup>
          )}
          {showProviderTypes && (
            <>
              <InputGroup>
                <MultiSelectChips
                  title='Associated Managed Care Options'
                  options={client?.managedProviderTypes ?? []}
                  values={values?.selectedManagedProviderTypes ?? []}
                  onChange={(v) => setValue('selectedManagedProviderTypes', v)}
                  getOptionValue={getId}
                  getOptionLabel={getDisplayName}
                  placeholder='All Managed Care Options'
                  disabled={disabled}
                />
              </InputGroup>
              <InputGroup>
                <MultiSelectChips
                  title='Associated Provided Care Options'
                  options={client?.providedProviderTypes ?? []}
                  values={values?.selectedProvidedProviderTypes ?? []}
                  onChange={(v) => setValue('selectedProvidedProviderTypes', v)}
                  getOptionValue={getId}
                  getOptionLabel={getDisplayName}
                  placeholder='All Provided Care Options'
                  disabled={disabled}
                />
              </InputGroup>
            </>
          )}
          {!isAdmin && isLeaf && (
            <InputGroup>
              <MultiSelectChips
                title='Associated Groups (select all that apply)'
                {...groupOpts}
                values={values?.selectedGroups ?? []}
                onChange={(v) => setValue('selectedGroups', v)}
                getOptionValue={getId}
                getOptionLabel={getName}
                placeholder='All Groups'
                disabled={disabled}
              />
            </InputGroup>
          )}
        </FormSection>

        <div style={{ display: 'flex', width: '100%' }}>
          {!isEditing && (
            <Button
              variant='outline-primary'
              onClick={() => submitAndReset(['name', 'email', 'credential'])}
              disabled={disabled || !dirty || !valid}>
              Save and Add Another
            </Button>
          )}
          <div style={{ flex: 1 }} />
          <ButtonGroup>
            <Button variant='ghost' onClick={goBack}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={disabled || !dirty || !valid}>
              {isEditing ? 'Update User' : 'Create User'}
            </Button>
          </ButtonGroup>
        </div>
      </Form>
    </AdminFormPage>
  );
}
